/**
 * Centralized resume data transformation and management utility
 * Consolidates data preparation logic from multiple files
 * Eliminates duplications in ResumePreview.jsx, CustomizableResumePreview.jsx, etc.
 */
import { sortResumeForDisplay } from "../DateSortingUtils";

/**
 * Helper function to safely get string value
 * @param {any} value - Value to convert to string
 * @returns {string} - Safe string value
 */
const safeString = (value) => {
  if (typeof value === "string") return value;
  if (value === null || value === undefined) return "";
  return String(value);
};

/**
 * Helper function to safely get array
 * @param {any} value - Value to convert to array
 * @returns {Array} - Safe array value
 */
const safeArray = (value) => {
  if (Array.isArray(value)) return value;
  return [];
};

/**
 * Helper function to safely get object
 * @param {any} value - Value to convert to object
 * @returns {Object} - Safe object value
 */
const safeObject = (value) => {
  if (value && typeof value === "object" && !Array.isArray(value)) return value;
  return {};
};

/**
 * Extract professional title from work experience
 * @param {Object} data - Resume data
 * @returns {string} - Professional title
 */
const extractProfessionalTitle = (data) => {
  const workExp = data.workExperience || data.experience || [];
  if (workExp.length > 0) {
    return workExp[0]?.jobTitle || workExp[0]?.title || "";
  }
  return "";
};

/**
 * Process personal information with enhanced field mapping
 * @param {Object} personalInfo - Raw personal info data
 * @returns {Object} - Processed personal info
 */
const processPersonalInfo = (personalInfo) => {
  const info = safeObject(personalInfo);

  // Handle name splitting if only full name is provided
  let firstName = info.firstName || "";
  let lastName = info.lastName || "";

  if (!firstName && !lastName && info.name) {
    const nameParts = info.name.split(" ");
    firstName = nameParts[0] || "";
    lastName = nameParts.slice(1).join(" ") || "";
  }

  return {
    firstName: safeString(firstName),
    lastName: safeString(lastName),
    fullName: safeString(info.name || `${firstName} ${lastName}`.trim()),
    email: safeString(info.email),
    phone: safeString(info.phone),
    location: safeString(info.location || info.address),
    linkedinUrl: safeString(info.linkedinUrl || info.linkedin || info.linkedInUrl || info.linkedIn),
    githubUrl: safeString(info.githubUrl || info.github),
    portfolioUrl: safeString(info.portfolioUrl || info.portfolio || info.website),
  };
};

/**
 * Process work experience with enhanced field mapping
 * @param {Array} workExperience - Raw work experience data
 * @returns {Array} - Processed work experience
 */
const processWorkExperience = (workExperience) => {
  return safeArray(workExperience).map((exp) => ({
    ...exp,
    jobTitle: safeString(exp?.jobTitle || exp?.title || exp?.position),
    company: safeString(exp?.company || exp?.employer),
    location: safeString(exp?.location),
    startDate: safeString(exp?.startDate),
    endDate: safeString(exp?.endDate),
    description: safeString(exp?.description),
    responsibilities: safeArray(exp?.responsibilities),
    achievements: safeArray(exp?.achievements),
    isCurrentJob: Boolean(exp?.isCurrentJob || exp?.current),
  }));
};

/**
 * Process education with enhanced field mapping
 * @param {Array} education - Raw education data
 * @returns {Array} - Processed education
 */
const processEducation = (education) => {
  return safeArray(education).map((edu) => ({
    ...edu,
    degree: safeString(edu?.degree),
    fieldOfStudy: safeString(edu?.fieldOfStudy || edu?.field || edu?.major),
    school: safeString(edu?.school || edu?.institution || edu?.university),
    location: safeString(edu?.location),
    startDate: safeString(edu?.startDate),
    endDate: safeString(edu?.endDate || edu?.graduationDate),
    gpa: safeString(edu?.gpa),
    honors: safeString(edu?.honors),
    description: safeString(edu?.description),
  }));
};

/**
 * Process skills with enhanced categorization
 * @param {Object|Array} skills - Raw skills data
 * @returns {Object} - Processed skills with categories
 */
const processSkills = (skills) => {
  if (!skills) return { technical: [], soft: [], tools: [], other: [], list: [] };

  // Handle flat array of skills
  if (Array.isArray(skills)) {
    return {
      technical: [],
      soft: [],
      tools: [],
      other: [],
      list: skills.map(safeString),
    };
  }

  // Handle categorized skills
  const skillsObj = safeObject(skills);
  const categorized = skillsObj.categorized || {};

  return {
    // Standard categories
    technical: safeArray(categorized.technical || skillsObj.technical),
    soft: safeArray(categorized.soft || skillsObj.soft),
    tools: safeArray(categorized.tools || skillsObj.tools),
    other: safeArray(categorized.other || skillsObj.other),
    // Additional categories that AI might return
    languages: safeArray(categorized.languages || skillsObj.languages),
    frameworks: safeArray(categorized.frameworks || skillsObj.frameworks),
    developer_tools: safeArray(categorized.developer_tools || skillsObj.developer_tools),
    // Flat list fallback
    list: safeArray(skillsObj.list),
    // Preserve the full categorized structure for templates that need it
    categorized: Object.keys(categorized).length > 0 ? categorized : null,
  };
};

/**
 * Process projects with enhanced field mapping
 * @param {Array} projects - Raw projects data
 * @returns {Array} - Processed projects
 */
const processProjects = (projects) => {
  return safeArray(projects).map((proj) => ({
    ...proj,
    name: safeString(proj?.name || proj?.title),
    description: safeString(proj?.description),
    technologies: safeString(proj?.technologies || safeArray(proj?.tech).join(", ")),
    url: safeString(proj?.url || proj?.link || proj?.demoUrl),
    link: safeString(proj?.link || proj?.url || proj?.demoUrl),
    githubUrl: safeString(proj?.githubUrl || proj?.github),
    duration: safeString(proj?.duration),
    startDate: safeString(proj?.startDate),
    endDate: safeString(proj?.endDate),
  }));
};

/**
 * Process certifications with enhanced field mapping
 * @param {Array} certifications - Raw certifications data
 * @returns {Array} - Processed certifications
 */
const processCertifications = (certifications) => {
  return safeArray(certifications).map((cert) => ({
    ...cert,
    name: safeString(cert?.name || cert?.title),
    authority: safeString(cert?.authority || cert?.issuer || cert?.organization),
    issuer: safeString(cert?.issuer || cert?.authority || cert?.organization),
    date: safeString(cert?.date || cert?.issueDate || cert?.earnedDate),
    expiryDate: safeString(cert?.expiryDate),
    credentialId: safeString(cert?.credentialId),
    url: safeString(cert?.url || cert?.link),
  }));
};

/**
 * Process other resume sections (achievements, languages, awards, etc.)
 * @param {Array} items - Raw items data
 * @param {string} type - Type of items (achievements, languages, awards, etc.)
 * @returns {Array} - Processed items
 */
const processOtherSections = (items, type = "generic") => {
  return safeArray(items).map((item) => {
    if (type === "achievements") {
      return {
        ...item,
        description: safeString(item?.description || item?.title),
        date: safeString(item?.date),
        organization: safeString(item?.organization),
      };
    }

    if (type === "languages") {
      return {
        ...item,
        name: safeString(item?.name || item?.language),
        proficiency: safeString(item?.proficiency || item?.level),
        certification: safeString(item?.certification),
      };
    }

    if (type === "awards") {
      return {
        ...item,
        title: safeString(item?.title || item?.name),
        issuer: safeString(item?.issuer || item?.organization || item?.awardedBy),
        date: safeString(item?.date || item?.awardDate),
        description: safeString(item?.description),
      };
    }

    if (type === "publications") {
      return {
        ...item,
        title: safeString(item?.title),
        authors: safeArray(item?.authors),
        journal: safeString(item?.journal || item?.publication),
        date: safeString(item?.date),
        url: safeString(item?.url || item?.link),
        description: safeString(item?.description),
      };
    }

    if (type === "references") {
      return {
        ...item,
        name: safeString(item?.name),
        title: safeString(item?.title || item?.jobTitle || item?.position),
        company: safeString(item?.company || item?.organization),
        email: safeString(item?.email),
        phone: safeString(item?.phone),
        relationship: safeString(item?.relationship),
      };
    }

    return {
      ...item,
      description: safeString(item?.description || item?.title || item?.name),
    };
  });
};

/**
 * Main function to prepare resume data for react-pdf templates
 * Consolidates logic from ResumePreview.jsx and CustomizableResumePreview.jsx
 * @param {Object} resumeData - Raw resume data from database
 * @param {Object} options - Processing options
 * @returns {Object|null} - Processed resume data for PDF generation
 */
export const prepareResumeDataForPDF = (resumeData, options = {}) => {
  if (!resumeData?.parsedData) return null;

  const data = resumeData.parsedData;

  // Apply date sorting if requested
  const sortedData = options.sortByDate ? sortResumeForDisplay(data) : data;

  const result = {
    // Personal Information
    personalInfo: processPersonalInfo(sortedData.personalInfo),

    // Summary/Objective
    summary: safeString(
      typeof sortedData.summary === "object" && sortedData.summary?.content
        ? sortedData.summary.content
        : sortedData.summary || sortedData.professionalSummary || sortedData.aboutMe || sortedData.objective
    ),

    // Professional title extracted from experience
    professionalTitle: safeString(extractProfessionalTitle(sortedData)),

    // Work Experience
    workExperience: processWorkExperience(sortedData.workExperience || sortedData.experience),

    // Education
    education: processEducation(sortedData.education),

    // Skills
    skills: processSkills(sortedData.skills),

    // Projects
    projects: processProjects(sortedData.projects),

    // Certifications
    certifications: processCertifications(sortedData.certifications),

    // Other sections
    achievements: processOtherSections(sortedData.achievements, "achievements"),
    languages: processOtherSections(sortedData.languages, "languages"),
    publications: processOtherSections(sortedData.publications, "publications"),
    awards: processOtherSections(sortedData.awards, "awards"),
    references: processOtherSections(sortedData.references, "references"),

    // New sections
    volunteering: safeArray(sortedData.volunteering).map((vol) => ({
      organization: safeString(vol?.organization),
      role: safeString(vol?.role),
      location: safeString(vol?.location),
      startDate: safeString(vol?.startDate),
      endDate: safeString(vol?.endDate),
      description: safeString(vol?.description),
      isCurrentRole: Boolean(vol?.isCurrentRole),
    })),

    interests: safeArray(sortedData.interests).map((interest) => ({
      name: safeString(interest?.name),
      description: safeString(interest?.description),
    })),

    industryExpertise: safeArray(sortedData.industryExpertise).map((exp) => ({
      name: safeString(exp?.name),
      proficiency: safeString(exp?.proficiency),
      yearsOfExperience: safeString(exp?.yearsOfExperience),
    })),

    // Profile Photo
    profilePhoto: resumeData.profilePhoto || null,

    // Custom Sections
    customSections: safeArray(sortedData.customSections)
      .map((section) => ({
        id: safeString(section?.id),
        title: safeString(section?.title),
        items: safeArray(section?.items).map((item) => ({
          content: safeString(item?.content),
        })),
      }))
      .filter((section) => section.id && section.title),

    // Section Titles - allows users to rename default section headers
    sectionTitles: sortedData.sectionTitles || {},

    // Metadata
    createdAt: resumeData.createdAt,
    updatedAt: resumeData.updatedAt,
    resumeId: resumeData._id,
  };

  return result;
};

/**
 * Transform resume data for display (non-PDF contexts)
 * @param {Object} resumeData - Raw resume data
 * @param {Object} options - Transformation options
 * @returns {Object|null} - Transformed resume data
 */
export const transformResumeForDisplay = (resumeData, options = {}) => {
  return prepareResumeDataForPDF(resumeData, { ...options, sortByDate: true });
};

/**
 * Extract specific section from resume data
 * @param {Object} resumeData - Resume data
 * @param {string} sectionName - Name of section to extract
 * @returns {any} - Section data
 */
export const extractResumeSection = (resumeData, sectionName) => {
  const processedData = prepareResumeDataForPDF(resumeData);
  return processedData?.[sectionName] || null;
};

/**
 * Validate resume data completeness
 * @param {Object} resumeData - Resume data to validate
 * @returns {Object} - Validation result with missing sections
 */
export const validateResumeData = (resumeData) => {
  const processedData = prepareResumeDataForPDF(resumeData);
  if (!processedData) return { isValid: false, missing: ["all"] };

  const requiredSections = ["personalInfo", "workExperience"];
  const recommendedSections = ["summary", "education", "skills"];

  const missing = [];
  const recommendations = [];

  requiredSections.forEach((section) => {
    if (
      !processedData[section] ||
      (Array.isArray(processedData[section]) && processedData[section].length === 0) ||
      (typeof processedData[section] === "string" && !processedData[section].trim())
    ) {
      missing.push(section);
    }
  });

  recommendedSections.forEach((section) => {
    if (
      !processedData[section] ||
      (Array.isArray(processedData[section]) && processedData[section].length === 0) ||
      (typeof processedData[section] === "string" && !processedData[section].trim())
    ) {
      recommendations.push(section);
    }
  });

  return {
    isValid: missing.length === 0,
    isComplete: missing.length === 0 && recommendations.length === 0,
    missing,
    recommendations,
    score: Math.max(0, 100 - missing.length * 30 - recommendations.length * 10),
  };
};

/**
 * Get resume statistics
 * @param {Object} resumeData - Resume data
 * @returns {Object} - Resume statistics
 */
export const getResumeStatistics = (resumeData) => {
  const processedData = prepareResumeDataForPDF(resumeData);
  if (!processedData) return null;

  return {
    workExperienceCount: processedData.workExperience?.length || 0,
    educationCount: processedData.education?.length || 0,
    projectsCount: processedData.projects?.length || 0,
    certificationsCount: processedData.certifications?.length || 0,
    skillsCount: processedData.skills?.list?.length || 0,
    hasProfilePicture: false, // TODO: Implement when profile pictures are added
    completionPercentage: validateResumeData(resumeData).score,
  };
};

// Default export for convenience
export default {
  prepareResumeDataForPDF,
  transformResumeForDisplay,
  extractResumeSection,
  validateResumeData,
  getResumeStatistics,
};
